import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {
  fetchSkuStatuses,
  getAreaMeasurementQuantites,
  getBrands,
  getColors,
  getCountries,
  getLengthMeasurementQuantites,
  getMaterials,
  getMeasurementObjects,
  getMeasurementUnits,
  getPallets,
  getProductGroups,
  getProductTypes,
  getVolumeMeasurementQuantites,
  getWeightMeasurementQuantites,
} from "../../selectors/skuSelectors";
import {selectCoefTypes, selectSaleRulesTypes} from "../../slices/CatalogsSlice";
import {selectAllUsers} from "../../slices/PersonSlice";
import {selectVedProductTypes} from "../VED/VedSetiingsGeneral/VedSettingsSlice";

const useSkuCatalogsService = () => {
  const {
    i18n,
    i18n: {language},
  } = useTranslation();

  const brands = useSelector(getBrands);
  const colors = useSelector(getColors);
  const countries = useSelector(getCountries);
  const materials = useSelector(getMaterials);
  const types = useSelector(getProductTypes);
  const groups = useSelector(getProductGroups);
  const measurementUnits = useSelector(getMeasurementUnits);
  const pallets = useSelector(getPallets);
  const measurementObjects = useSelector(getMeasurementObjects);
  // skuOptions = useSelector(getSkuOptionsList),
  const lengthMQ = useSelector(getLengthMeasurementQuantites);
  const areaMQ = useSelector(getAreaMeasurementQuantites);
  const volumeMQ = useSelector(getVolumeMeasurementQuantites);
  const weightMq = useSelector(getWeightMeasurementQuantites);
  const statuses = useSelector(fetchSkuStatuses);

  const saleRulesTypes = useSelector(selectSaleRulesTypes);
  const coefTypes = useSelector(selectCoefTypes);
  const users = useSelector(selectAllUsers);
  const vedProductTypes = useSelector(selectVedProductTypes);

  const namingType = useMemo(() => (i18n.language === "en" ? "engName" : "name"), [i18n.language]);

  const getTranslation = item => {
    if (language === "en") {
      return item?.engName || `${item?.name} (translation missing)`;
    }

    return item?.name || `${item?.engName} (немає перекладу)`;
  };

  const brandList = useMemo(
      () =>
        brands.map(item => {
          return {title: item.name, value: item.factoryId, disabled: !item?.isAvailable};
        }),
      [brands],
    ),
    colorsList = useMemo(
      () =>
        colors
          ? colors.map(item => {
              return {title: getTranslation(item), value: item?.colorId};
            })
          : [],
      [colors, namingType],
    ),
    countriesList = useMemo(
      () =>
        countries.map(item => {
          return {title: item.shortName.replace(/ +/g, " ").trim(), value: item.countryId};
        }),
      [countries],
    ),
    materialsList = useMemo(
      () =>
        materials
          ? materials.map(item => {
              return {title: getTranslation(item), value: item?.materialId};
            })
          : [],
      [materials, namingType],
    ),
    measurementUnitsList = useMemo(
      () =>
        measurementUnits.map(item => {
          return {title: item[namingType], value: item.unitId};
        }),
      [measurementUnits, namingType],
    ),
    productGroups = useMemo(
      () =>
        groups
          ?.filter(({deprecated}) => !deprecated)
          ?.map(item => {
            return {
              title: getTranslation(item),
              value: item.productGroupId,
            };
          }),
      [groups, language],
    ),
    productTypes = useMemo(
      () =>
        types.map(item => {
          return {title: getTranslation(item), value: item.productTypeId};
        }),
      [types, namingType],
    ),
    palletsList = useMemo(
      () =>
        pallets.map(item => {
          return {title: item.name, value: item.palletId, length: item.length, width: item.width};
        }),
      [pallets, namingType],
    ),
    measurementObjectsList = useMemo(
      () =>
        measurementObjects.map(item => {
          return {title: item[namingType], value: item.objectId};
        }),
      [pallets, namingType],
    ),
    lengthMeasurementQuantites = useMemo(
      () =>
        lengthMQ.map(item => {
          return {title: item[namingType], value: item.unitId, multiplier: item.multiplier};
        }),
      [lengthMQ, namingType],
    ),
    areaMeasurementQuantites = useMemo(
      () =>
        areaMQ.map(item => {
          return {title: item[namingType], value: item.unitId, multiplier: item.multiplier};
        }),
      [areaMQ, namingType],
    ),
    volumeMeasurementQuantites = useMemo(
      () =>
        volumeMQ.map(item => {
          return {title: item[namingType], value: item.unitId, multiplier: item.multiplier};
        }),
      [volumeMQ, namingType],
    ),
    weightMeasurementQuantites = useMemo(
      () =>
        weightMq.map(item => {
          return {title: item[namingType], value: item.unitId, multiplier: item.multiplier};
        }),
      [weightMq, namingType],
    );

  const coefTypesOptions =
    coefTypes?.map(({coeficientTypeId, nameEng, nameUkr}) => ({
      value: coeficientTypeId,
      title: language === "en" ? nameEng : nameUkr,
    })) || [];
  const saleRulesOptions =
    saleRulesTypes?.map(({saleRulesTypeId, nameEng, nameUkr}) => ({
      value: saleRulesTypeId,
      title: language === "en" ? nameEng : nameUkr,
    })) || [];
  const managersOptions =
    users?.map(({userId, name}) => ({
      value: userId,
      title: name,
    })) || [];
  const vedProductTypeOptions = vedProductTypes.map(item => {
    return {title: item[namingType], value: item.productTypeId};
  });
  const statusOptions =
    statuses?.map(({statusId, engName, ukrName}) => ({
      value: statusId,
      title: language === "en" ? engName : ukrName,
    })) ?? [];

  return {
    brandList,
    colorsList,
    countriesList,
    materialsList,
    productGroups,
    productTypes,
    measurementUnitsList,
    palletsList,
    measurementObjectsList,
    weightMeasurementQuantites,
    volumeMeasurementQuantites,
    areaMeasurementQuantites,
    lengthMeasurementQuantites,
    coefTypesOptions,
    saleRulesOptions,
    managersOptions,
    vedProductTypeOptions,
    statusOptions,
  };
};

export default useSkuCatalogsService;
